import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import auth from '../auth';
import firestore from '../firestore';
import { callAPI } from './api';

type GoogleTokenResponse = {
	access_token?: string;
	error?: string;
	error_description?: string;
};

type GoogleTokenClient = {
	requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
};

type GoogleProfile = {
	email?: string;
};

type GoogleAuthPendingAction = 'create_password' | 'link_password';

export type PendingGoogleAuth = {
	action: GoogleAuthPendingAction;
	email: string;
	credential: firebase.auth.OAuthCredential;
};

export type GoogleAuthResult =
	| { success: true }
	| { success: false; error: string; firebaseDown?: boolean }
	| { success: false; pending: PendingGoogleAuth };

declare global {
	interface Window {
		google?: {
			accounts?: {
				oauth2?: {
					initTokenClient: (config: {
						client_id: string;
						scope: string;
						prompt?: string;
						callback: (response: GoogleTokenResponse) => void;
						error_callback?: (error: { type?: string; message?: string }) => void;
					}) => GoogleTokenClient;
				};
			};
		};
	}
}

const GOOGLE_PROVIDER_ID = 'google.com';
const PASSWORD_PROVIDER_ID = 'password';
const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services';

function getGoogleClientId() {
	return process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
}

function getAuthErrorCode(error: unknown) {
	return typeof error === 'object' && error && 'code' in error ? String(error.code) : '';
}

function getAuthErrorMessage(error: unknown) {
	return typeof error === 'object' && error && 'message' in error ? String(error.message) : '';
}

function isNetworkError(error: unknown) {
	const message = getAuthErrorMessage(error).toLowerCase();
	return message.includes('network') || getAuthErrorCode(error) === 'auth/visibility-check-was-unavailable';
}

async function loadGoogleIdentityServices() {
	if (window.google?.accounts?.oauth2) return;

	await new Promise<void>((resolve, reject) => {
		const existingScript = document.getElementById(GOOGLE_IDENTITY_SCRIPT_ID) as HTMLScriptElement | null;
		if (existingScript) {
			existingScript.addEventListener('load', () => resolve(), { once: true });
			existingScript.addEventListener('error', () => reject(new Error('Google Identity Services failed to load')), { once: true });
			return;
		}

		const script = document.createElement('script');
		script.id = GOOGLE_IDENTITY_SCRIPT_ID;
		script.src = 'https://accounts.google.com/gsi/client';
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error('Google Identity Services failed to load'));
		document.head.appendChild(script);
	});

	if (!window.google?.accounts?.oauth2) throw new Error('Google Identity Services is not available');
}

async function requestGoogleAccessToken() {
	const clientId = getGoogleClientId();
	if (!clientId) throw new Error('Google Sign-In no está configurado.');

	await loadGoogleIdentityServices();

	return await new Promise<string>((resolve, reject) => {
		const client = window.google!.accounts!.oauth2!.initTokenClient({
			client_id: clientId,
			scope: 'openid email profile',
			prompt: 'select_account',
			callback: (response) => {
				if (response.error) {
					reject(new Error(response.error_description || response.error));
					return;
				}

				if (!response.access_token) {
					reject(new Error('No se pudo autenticar con Google.'));
					return;
				}

				resolve(response.access_token);
			},
			error_callback: (error) => {
				reject(new Error(error.message || error.type || 'No se pudo autenticar con Google.'));
			}
		});

		client.requestAccessToken({ prompt: 'select_account' });
	});
}

async function getGoogleEmail(accessToken: string) {
	const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
		headers: { Authorization: `Bearer ${accessToken}` }
	});

	if (!response.ok) throw new Error('No se pudo obtener el correo de Google.');

	const profile = (await response.json()) as GoogleProfile;
	if (!profile.email) throw new Error('La cuenta de Google no tiene correo disponible.');

	return profile.email.toLowerCase();
}

async function storeExists(email: string) {
	return (await firestore.collection('Stores').doc(email).get()).exists;
}

export async function verifyPortalEmail(email: string) {
	const [, error] = await callAPI('https://api.krece.app/registration/verifyuseremail/', 'POST', { email }, null, null, false);
	if (error) throw new Error(error);
}

async function signInWithGoogleCredential(credential: firebase.auth.OAuthCredential) {
	await auth.signInWithCredential(credential);
}

async function linkGoogleCredential(credential: firebase.auth.OAuthCredential) {
	try {
		if (!auth.currentUser) throw new Error('No hay una sesión activa para conectar Google.');
		await auth.currentUser.linkWithCredential(credential);
	} catch (error) {
		const code = getAuthErrorCode(error);
		if (code === 'auth/provider-already-linked') return;
		if (code === 'auth/credential-already-in-use') {
			await signInWithGoogleCredential(credential);
			return;
		}
		throw error;
	}
}

function getFriendlyGoogleError(error: unknown) {
	const code = getAuthErrorCode(error);
	const message = getAuthErrorMessage(error).toLowerCase();

	if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request' || message.includes('popup_closed')) {
		return 'Inicio de sesión con Google cancelado.';
	}

	if (isNetworkError(error)) {
		return 'Mala conexión: Por favor chequee su conexión a internet.';
	}

	return 'No se pudo iniciar sesión con Google. Intente nuevamente.';
}

export function useGoogleAuth() {
	const startGoogleSignIn = async (): Promise<GoogleAuthResult> => {
		try {
			const accessToken = await requestGoogleAccessToken();
			const email = await getGoogleEmail(accessToken);

			if (!(await storeExists(email))) {
				return {
					success: false,
					error: 'Esta tienda no tiene cuenta con Krece. Por favor cree una cuenta.'
				};
			}

			const credential = firebase.auth.GoogleAuthProvider.credential(null, accessToken);
			const methods = await auth.fetchSignInMethodsForEmail(email);

			if (!methods.length) {
				return { success: false, pending: { action: 'create_password', email, credential } };
			}

			if (methods.includes(GOOGLE_PROVIDER_ID)) {
				await signInWithGoogleCredential(credential);
				return { success: true };
			}

			if (!methods.includes(PASSWORD_PROVIDER_ID)) {
				return {
					success: false,
					error: 'Este correo ya existe con otro método de acceso. Contacte a soporte.'
				};
			}

			await verifyPortalEmail(email);

			try {
				await signInWithGoogleCredential(credential);
				return { success: true };
			} catch (error) {
				if (getAuthErrorCode(error) === 'auth/account-exists-with-different-credential') {
					return { success: false, pending: { action: 'link_password', email, credential } };
				}
				throw error;
			}
		} catch (error) {
			return {
				success: false,
				error: getFriendlyGoogleError(error),
				firebaseDown: getAuthErrorCode(error) === 'auth/visibility-check-was-unavailable'
			};
		}
	};

	const completeGoogleLink = async (pending: PendingGoogleAuth, password: string): Promise<GoogleAuthResult> => {
		try {
			await auth.signInWithEmailAndPassword(pending.email, password);
			await verifyPortalEmail(pending.email);
			await linkGoogleCredential(pending.credential);
			return { success: true };
		} catch (error) {
			return {
				success: false,
				error: getAuthErrorCode(error) === 'auth/wrong-password' || getAuthErrorCode(error) === 'auth/invalid-credential'
					? 'Email o contraseña incorrecta.'
					: getFriendlyGoogleError(error),
				firebaseDown: getAuthErrorCode(error) === 'auth/visibility-check-was-unavailable'
			};
		}
	};

	const completeGooglePasswordCreation = async (pending: PendingGoogleAuth, password: string): Promise<GoogleAuthResult> => {
		try {
			if (!(await storeExists(pending.email))) {
				return {
					success: false,
					error: 'Esta tienda no ha sido autorizada por Krece. Para afiliar su tienda por favor comuníquese con nosotros al +58 4149022115.'
				};
			}

			await auth.createUserWithEmailAndPassword(pending.email, password);
			await verifyPortalEmail(pending.email);
			await linkGoogleCredential(pending.credential);
			return { success: true };
		} catch (error) {
			const code = getAuthErrorCode(error);
			if (code === 'auth/email-already-in-use') {
				return { success: false, pending: { ...pending, action: 'link_password' } };
			}

			return {
				success: false,
				error: code === 'auth/weak-password'
					? 'La contraseña debe tener al menos 6 caracteres.'
					: getFriendlyGoogleError(error),
				firebaseDown: code === 'auth/visibility-check-was-unavailable'
			};
		}
	};

	return { startGoogleSignIn, completeGoogleLink, completeGooglePasswordCreation };
}
